import type { Language } from '@/app/i18n'
import { currencyExponent, type Money } from './money'

type PluralForm = 'one' | 'few' | 'many'
type Forms = Readonly<Record<PluralForm, string>>

interface Speller {
  zero: string
  group: (value: number, scale: number) => string
  scale: (value: number, scale: number) => string
  separator: string
  plural: (value: number) => PluralForm
  phrase: (major: string, minor: string | null) => string
}

const forms = (one: string, few = one, many = few): Forms => ({ one, few, many })

const MAX_SCALE = 4

function joinNonEmpty(parts: readonly string[], separator: string): string {
  return parts.filter(Boolean).join(separator)
}

const EN_ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const EN_TEENS = [
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
]
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']
const EN_SCALES = ['', 'thousand', 'million', 'billion', 'trillion']

function englishTens(value: number): string {
  if (value < 10) return EN_ONES[value]!
  if (value < 20) return EN_TEENS[value - 10]!
  return joinNonEmpty([EN_TENS[Math.floor(value / 10)]!, EN_ONES[value % 10]!], '-')
}

const english: Speller = {
  zero: 'zero',
  group: (value) => {
    const hundreds = Math.floor(value / 100)
    return joinNonEmpty([hundreds ? `${EN_ONES[hundreds]!} hundred` : '', englishTens(value % 100)], ' ')
  },
  scale: (_, scale) => EN_SCALES[scale]!,
  separator: ' ',
  plural: (value) => (value === 1 ? 'one' : 'many'),
  phrase: (major, minor) => `${minor ? `${major} and ${minor}` : major} only`,
}

const UZ_ONES = ['', 'bir', 'ikki', 'uch', "to'rt", 'besh', 'olti', 'yetti', 'sakkiz', "to'qqiz"]
const UZ_TENS = ['', "o'n", 'yigirma', "o'ttiz", 'qirq', 'ellik', 'oltmish', 'yetmish', 'sakson', "to'qson"]
const UZ_SCALES = ['', 'ming', 'million', 'milliard', 'trillion']

const uzbek: Speller = {
  zero: 'nol',
  group: (value) => {
    const hundreds = Math.floor(value / 100)
    const tens = Math.floor((value % 100) / 10)
    return joinNonEmpty([hundreds ? `${UZ_ONES[hundreds]!} yuz` : '', UZ_TENS[tens]!, UZ_ONES[value % 10]!], ' ')
  },
  scale: (_, scale) => UZ_SCALES[scale]!,
  separator: ' ',
  plural: () => 'one',
  phrase: (major, minor) => joinNonEmpty([major, minor ?? ''], ' '),
}

const RU_ONES = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять']
const RU_ONES_FEMININE = ['', 'одна', 'две', ...RU_ONES.slice(3)]
const RU_TEENS = [
  'десять',
  'одиннадцать',
  'двенадцать',
  'тринадцать',
  'четырнадцать',
  'пятнадцать',
  'шестнадцать',
  'семнадцать',
  'восемнадцать',
  'девятнадцать',
]
const RU_TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто']
const RU_HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот']
const RU_SCALES: readonly Forms[] = [
  forms(''),
  forms('тысяча', 'тысячи', 'тысяч'),
  forms('миллион', 'миллиона', 'миллионов'),
  forms('миллиард', 'миллиарда', 'миллиардов'),
  forms('триллион', 'триллиона', 'триллионов'),
]

function russianPlural(value: number): PluralForm {
  const lastTwo = value % 100
  const last = value % 10
  if (lastTwo >= 11 && lastTwo <= 14) return 'many'
  if (last === 1) return 'one'
  if (last >= 2 && last <= 4) return 'few'
  return 'many'
}

const russian: Speller = {
  zero: 'ноль',
  group: (value, scale) => {
    const rest = value % 100
    const ones = scale === 1 ? RU_ONES_FEMININE : RU_ONES
    const tail =
      rest >= 10 && rest < 20
        ? [RU_TEENS[rest - 10]!]
        : [RU_TENS[Math.floor(rest / 10)]!, ones[rest % 10]!]
    return joinNonEmpty([RU_HUNDREDS[Math.floor(value / 100)]!, ...tail], ' ')
  },
  scale: (value, scale) => RU_SCALES[scale]![russianPlural(value)],
  separator: ' ',
  plural: russianPlural,
  phrase: (major, minor) => joinNonEmpty([major, minor ?? ''], ' '),
}

const FA_ONES = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه']
const FA_TEENS = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نزده']
const FA_TENS = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود']
const FA_HUNDREDS = ['', 'یکصد', 'دوصد', 'سه‌صد', 'چهارصد', 'پنجصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد']
const FA_SCALES = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون']
const FA_AND = ' و '

const dari: Speller = {
  zero: 'صفر',
  group: (value) => {
    const rest = value % 100
    const tail =
      rest >= 10 && rest < 20 ? [FA_TEENS[rest - 10]!] : [FA_TENS[Math.floor(rest / 10)]!, FA_ONES[rest % 10]!]
    return joinNonEmpty([FA_HUNDREDS[Math.floor(value / 100)]!, ...tail], FA_AND)
  },
  scale: (_, scale) => FA_SCALES[scale]!,
  separator: FA_AND,
  plural: () => 'one',
  phrase: (major, minor) => joinNonEmpty([major, minor ?? ''], FA_AND),
}

const SPELLERS: Readonly<Record<Language, Speller>> = { en: english, uz: uzbek, ru: russian, fa: dari }

interface CurrencyNames {
  major: Forms
  minor: Forms
}

const CURRENCY_NAMES: Readonly<Record<string, Readonly<Record<Language, CurrencyNames>>>> = {
  AFN: {
    en: { major: forms('afghani', 'afghanis'), minor: forms('pul', 'puls') },
    uz: { major: forms("afg'oni"), minor: forms('pul') },
    ru: { major: forms('афгани'), minor: forms('пул', 'пула', 'пулов') },
    fa: { major: forms('افغانی'), minor: forms('پول') },
  },
  UZS: {
    en: { major: forms('sum', 'sums'), minor: forms('tiyin', 'tiyins') },
    uz: { major: forms("so'm"), minor: forms('tiyin') },
    ru: { major: forms('сум', 'сума', 'сумов'), minor: forms('тийин', 'тийина', 'тийинов') },
    fa: { major: forms('سوم'), minor: forms('تیین') },
  },
  USD: {
    en: { major: forms('dollar', 'dollars'), minor: forms('cent', 'cents') },
    uz: { major: forms('dollar'), minor: forms('sent') },
    ru: { major: forms('доллар', 'доллара', 'долларов'), minor: forms('цент', 'цента', 'центов') },
    fa: { major: forms('دالر'), minor: forms('سنت') },
  },
}

function spellNumber(value: number, speller: Speller): string {
  if (value === 0) return speller.zero
  const parts: string[] = []
  let rest = value
  for (let scale = 0; rest > 0; scale++) {
    const group = rest % 1000
    if (group > 0) parts.unshift(joinNonEmpty([speller.group(group, scale), speller.scale(group, scale)], ' '))
    rest = Math.floor(rest / 1000)
  }
  return parts.join(speller.separator)
}

function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1)
}

/** «Four hundred ten thousand afghanis and fifty puls only»; nomi ma'lum bo'lmagan valyutada ISO kod va «50/100» */
export function amountInWords(value: Money, language: Language): string {
  const exponent = currencyExponent(value.currency)
  const base = 10 ** exponent
  const minor = value.amount % base
  const major = (value.amount - minor) / base
  if (!Number.isSafeInteger(value.amount) || value.amount < 0 || major >= 1000 ** (MAX_SCALE + 1)) {
    throw new RangeError(`Summani so'z bilan yozib bo'lmaydi: ${String(value.amount)}`)
  }
  const speller = SPELLERS[language]
  const names = CURRENCY_NAMES[value.currency]?.[language]

  const named = (amount: number, unit: Forms) => `${spellNumber(amount, speller)} ${unit[speller.plural(amount)]}`
  const majorText = names ? named(major, names.major) : `${spellNumber(major, speller)} ${value.currency}`
  const minorText =
    minor === 0 ? null : names ? named(minor, names.minor) : `${String(minor).padStart(exponent, '0')}/${String(base)}`
  return capitalize(speller.phrase(majorText, minorText))
}
